/* eslint-disable @next/next/no-img-element */
import * as React from "react";
import { ToasterEmbed } from "@toaster/pantry/react";
import { Subhead } from "@bloomberg/media-ui-article";

import { Chart } from "~types/wardenclyffe";
import { decodeHtmlEntities } from "~utils/stringUtil";

import styles from "./styles.module.css";

export const TOASTER_IFRAME_BLUE_ORIGIN = "origin.blue.bloomberg.com";

export type ToasterChartProps = {
    id: string;
    alt: string;
    src: string;
    chart: Chart;
    theme?: string | null;
};

export function resolveIframeUrl({ baseUrl }: { baseUrl?: string | null }) {
    if (!baseUrl) return "";

    const chartUrl = new URL(baseUrl);
    chartUrl.protocol = "https:";

    // if origin.blue, swap hosts client-side
    // iframe host to resolve via origin.blue
    if (process.env.NEXT_PUBLIC_COLOR_ENV === "blue") {
        chartUrl.host = TOASTER_IFRAME_BLUE_ORIGIN;
    }

    return chartUrl.toString();
}

export function ToasterChart(props: ToasterChartProps) {
    const { alt, src, chart } = props;
    const { url, title, subtitle, footnote, source, uuid } = chart;

    const resolvedIframeUrl = resolveIframeUrl({
        baseUrl: url,
    });

    // don't render a chart if we don't have a url
    if (!resolvedIframeUrl) return null;

    return (
        <figure
            className={styles["chart-container"]}
            data-component="toaster-chart"
        >
            <div className={styles["text-group"]}>
                {title && (
                    <Subhead className={styles.title}>
                        {decodeHtmlEntities(title)}
                    </Subhead>
                )}
                {subtitle && (
                    <p
                        className={styles.subtitle}
                        data-component="toaster-subtitle"
                    >
                        {decodeHtmlEntities(subtitle)}
                    </p>
                )}
            </div>
            <noscript>
                <img src={src} alt={alt} loading="lazy" />
            </noscript>
            <ToasterEmbed
                id={uuid}
                src={resolvedIframeUrl}
                titles={false}
                // set to "dark" if enabling dark mode
                variant="light"
            />
            <figcaption className={styles.caption}>
                {source && <p dangerouslySetInnerHTML={{ __html: source }} />}
                {footnote && <p>{decodeHtmlEntities(footnote)}</p>}
            </figcaption>
        </figure>
    );
}
